import React from 'react'
import { Helmet } from 'react-helmet'

const BlogSchema = ({ post, author, siteUrl }) => {
  if (!post) return null

  const extractTextContent = (htmlContent) => {
    if (!htmlContent) return ''
    return htmlContent
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const contentText = extractTextContent(post.content?.html)
  const excerptText = extractTextContent(post.excerpt)

  // Create Speakable markup
  const speakableSchema = {
    "@type": "SpeakableSpecification",
    "cssSelector": [
      "h1.topic",
      ".excerpt p",
      ".blog-single-main-content h2",
      ".blog-single-main-content h3",
      ".blog-single-main-content p"
    ]
  }

  // Create Organization schema for the website
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${siteUrl}/#organization`,
    "name": "SuperOps",
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": "https://us-west-2.graphassets.com/AsRMKMrtKTFW6TGbr4KgUz/cme9mk4b1dezl07mx466oe8oz"
    },
    "sameAs": [
      "https://x.com/Superops_com",
      "https://www.instagram.com/superops.ai/",
      "https://www.linkedin.com/company/superops-ai/about/",
      "https://www.facebook.com/pages/category/Software-Company/Superopsai-111692850544037/",
      "https://www.youtube.com/@SuperOps"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": "English"
    }
  }

  // Create WebSite schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    "name": "SuperOps Blog",
    "url": `${siteUrl}/blog`,
    "description": "Latest insights, tips, and updates from the SuperOps team",
    "publisher": {
      "@id": `${siteUrl}/#organization`
    }
  }

  // Create WebPage schema for the blog post page
  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/blog/${post.slug}`,
    "name": post.title,
    "description": post.excerpt || excerptText,
    "url": `${siteUrl}/blog/${post.slug}`,
    "primaryImageOfPage": post.coverImage?.url ? {
      "@type": "ImageObject",
      "url": post.coverImage.url,
      "width": post.coverImage.width,
      "height": post.coverImage.height
    } : undefined,
    "mainEntity": {
      "@id": `${siteUrl}/blog/${post.slug}#article`
    },
    "breadcrumb": {
      "@id": `${siteUrl}/blog/${post.slug}#breadcrumb`
    },
    "publisher": {
      "@id": `${siteUrl}/#organization`
    },
    "inLanguage": "en",
    "datePublished": post.date ? new Date(post.date).toISOString() : new Date().toISOString(),
    "dateModified": post.date ? new Date(post.date).toISOString() : new Date().toISOString()
  }

  // Create Person schema for the author
  const authorSchema = author?.name ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": author?.slug ? `${siteUrl}/blog/author/${author.slug}#person` : `${siteUrl}/blog/author/arvind-parthiban#person`,
    "name": author.name,
    "url": author?.slug ? `${siteUrl}/blog/author/${author.slug}` : undefined,
    "jobTitle": author.title,
    "image": author.picture?.url,
    "sameAs": [
      ...(author?.twitterUrl ? [author.twitterUrl] : []),
      ...(author?.linkedInUrl ? [author.linkedInUrl] : []),
      ...(author?.portfolioUrl ? [author.portfolioUrl] : [])
    ].filter(Boolean),
    "worksFor": {
      "@id": `${siteUrl}/#organization`
    }
  } : {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${siteUrl}/blog/author/arvind-parthiban#person`,
    "name": "SuperOps Team",
    "worksFor": {
      "@id": `${siteUrl}/#organization`
    }
  }

  // Create BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${siteUrl}/blog/${post.slug}#breadcrumb`,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": {
          "@id": `${siteUrl}/#website`
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": `${siteUrl}/blog`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `${siteUrl}/blog/${post.slug}`
      },
    ]
  }

  // Create BlogPosting schema markup
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${siteUrl}/blog/${post.slug}#article`,
    "headline": post.title,
    "description": post.excerpt || excerptText,
    "image": post.coverImage?.url ? [{
      "@type": "ImageObject",
      "url": post.coverImage.url,
      "width": post.coverImage.width,
      "height": post.coverImage.height
    }] : [],
    "author": {
      "@id": author?.slug ? `${siteUrl}/blog/author/${author.slug}#person` : `${siteUrl}/blog/author/arvind-parthiban#person`
    },
    "publisher": {
      "@id": `${siteUrl}/#organization`
    },
    "datePublished": post.date ? new Date(post.date).toISOString() : new Date().toISOString(),
    "dateModified": post.date ? new Date(post.date).toISOString() : new Date().toISOString(),
    "mainEntityOfPage": {
      "@id": `${siteUrl}/blog/${post.slug}`
    },
    "breadcrumb": {
      "@id": `${siteUrl}/blog/${post.slug}#breadcrumb`
    },
    ...(post.tags?.[0] && { "articleSection": post.tags[0] }),
    ...(post.tags?.length > 0 && { "keywords": post.tags.join(', ') }),
    ...(post.seo?.keywords && { "keywords": post.seo.keywords }),
    "wordCount": contentText.split(' ').length,
    "timeRequired": post.readTime ? `PT${post.readTime}M` : undefined,
    "articleBody": contentText,
    "inLanguage": "en",
    "speakable": speakableSchema,
    "potentialAction": [
      {
        "@type": "ReadAction",
        "target": `${siteUrl}/blog/${post.slug}`
      }
    ]
  }

  // Combine all schemas into a single JSON-LD block
  const combinedSchema = [
    organizationSchema,
    websiteSchema,
    webpageSchema,
    breadcrumbSchema,
    ...(authorSchema ? [authorSchema] : []),
    articleSchema
  ]

  return (
    <Helmet>
      {/* Combined Schema with @id references */}
      <script type="application/ld+json">
        {JSON.stringify(combinedSchema)}
      </script>
    </Helmet>
  )
}

export default BlogSchema 